/**
 * The Excel-parity keyboard shim's single source of truth: which key belongs
 * to which lane, which action id it maps to, and which actions mutate
 * the workbook (gated off during a history preview).
 *
 * Lane A (`onKeyDownCapture` on the grid wrapper) only ever fires when
 * `isGridKeyTarget` holds — the library's own Find/dialog/add-rows inputs and
 * the sheet-tab rename box are never intercepted. Lane B (a document capture
 * listener) handles Save/Print/Escape plus the focus-lost fallback, which is
 * driven by `isFallbackTarget`, not a key match, and lives outside this table
 * (its condition is the event target, not the key — see `useSheetFocus.ts`).
 */
import { CELL_INPUT_ID, FORMULA_BAR_ID } from './selectors';

/** Everything `matchShortcut` needs off a KeyboardEvent — kept minimal so
 *  pure unit tests can pass plain object literals instead of real events. */
export interface KeyLike {
    key: string;
    code?: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
}

/** State `matchShortcut` needs beyond the key itself. Only `editing` is used
 *  today; the point-mode/F4 matchers may need to narrow further (e.g.
 *  caret position), which they can add here without touching Lane A's target gate. */
export interface ShortcutMatchState {
    editing: boolean;
}

// Lane A (grid) action ids. Unimplemented ones dispatch to a
// shared no-op stub in useSheetKeyboardShortcuts.ts until their owning phase
// wires in the real handler.
export type LaneAActionId =
    | 'swallowSheetFocusToggle'
    | 'pointMode'
    | 'cycleRef'
    | 'fillSelection'
    | 'selectColumns'
    | 'selectRows'
    | 'switchSheet'
    | 'navHome'
    | 'navCtrlHome'
    | 'navCtrlEnd'
    | 'openGoTo'
    | 'openFormatCells'
    | 'toggleFilter'
    | 'autoSum'
    | 'pasteValues'
    | 'openPasteSpecial'
    | 'enterMove'
    | 'tabCommit'
    | 'italic'
    | 'underline'
    | 'extendHome'
    | 'extendCtrlHome'
    | 'extendCtrlEnd'
    | 'deleteCells'
    | 'insertCells'
    | 'hideRows'
    | 'hideCols'
    | 'unhideRows'
    | 'unhideCols'
    | 'insertDate'
    | 'insertTime'
    | 'copyAbove'
    | 'insertLink'
    | 'numFmtPreset'
    | 'openCellDropdown';

// Lane B (document) ids — save/printGuard are mine; Escape is resolved via
// escapePolicy.ts's own richer state, not a plain ActionId dispatch, but it
// still occupies a Lane B key-match slot ("except for S/P/Esc").
export type LaneBActionId = 'save' | 'printGuard' | 'resolveEscape';

/** The fill-handle double-click is a mouse binding (`onDoubleClickCapture`),
 *  not a keydown — it has no place in a keydown-matched lane table, so it is
 *  its own id, dispatched directly by the double-click handler. */
export type MouseActionId = 'fillDownDblClick';

export type ActionId = LaneAActionId | LaneBActionId | MouseActionId;

interface ShortcutBinding<T extends string> {
    id: T;
    /** True when the action writes to the workbook — gated off during a
     *  history preview. */
    mutating: boolean;
    matches: (e: KeyLike, state: ShortcutMatchState) => boolean;
}

const isArrowKey = (key: string): boolean => key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
const lower = (key: string): string => key.toLowerCase();

/** Frozen Lane A binding table. `Object.keys` is exactly this table's
 *  action ids — see `shortcutKeymap.test.ts`. */
export const LANE_A: Readonly<Record<LaneAActionId, ShortcutBinding<LaneAActionId>>> = Object.freeze({
    swallowSheetFocusToggle: {
        id: 'swallowSheetFocusToggle',
        mutating: false,
        matches: (e) => lower(e.key) === 'f' && Boolean(e.ctrlKey) && Boolean(e.shiftKey) && !e.altKey,
    },
    pointMode: {
        id: 'pointMode',
        mutating: true,
        matches: (e, s) => s.editing && isArrowKey(e.key),
    },
    cycleRef: {
        id: 'cycleRef',
        mutating: true,
        // Unlike pointMode, F4 is not gated on `editing` — the library's own
        // F4 handler is a permanent empty-body no-op in every state (root
        // cause table), so there is no "not editing" behaviour to protect,
        // and the history-preview gate must catch F4 even though a
        // read-only preview is never in an editing state at all. Mac Cmd+T IS
        // gated on `editing` — unlike F4 it is a genuine browser-reserved key
        // (new tab), so it must only be claimed while actually in a formula
        // edit; handleCycleRef's own no-ref-under-caret check declines the
        // rest (dispatchLaneA then leaves it unhandled for the browser).
        matches: (e, s) => e.key === 'F4' || (lower(e.key) === 't' && Boolean(e.metaKey) && !e.ctrlKey && !e.shiftKey && s.editing),
    },
    fillSelection: {
        id: 'fillSelection',
        mutating: true,
        // Ctrl+Enter (Win), and Mac Cmd+Return / Ctrl+Return (Excel Mac).
        matches: (e) => e.key === 'Enter' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    selectColumns: {
        id: 'selectColumns',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === ' ' && Boolean(e.ctrlKey) && !e.metaKey && !e.shiftKey,
    },
    selectRows: {
        id: 'selectRows',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === ' ' && Boolean(e.shiftKey) && !e.ctrlKey && !e.metaKey,
    },
    switchSheet: {
        id: 'switchSheet',
        mutating: false,
        // Alt+↑/↓ and Ctrl+Shift+PgUp/PgDn are no longer switchSheet — Alt+↓
        // is openCellDropdown instead, and Ctrl+Shift+PgUp/PgDn (group-select
        // sheets) has no app equivalent.
        matches: (e, s) => {
            if (s.editing) return false;
            const ctrlOrMeta = Boolean(e.ctrlKey || e.metaKey);
            if ((e.key === 'PageUp' || e.key === 'PageDown') && ctrlOrMeta && !e.shiftKey) return true; // Ctrl+PgUp/PgDn
            if ((e.key === 'PageUp' || e.key === 'PageDown') && e.altKey && !ctrlOrMeta && !e.shiftKey) return true; // Alt+PgUp/PgDn fallback
            if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && e.altKey && !ctrlOrMeta && !e.shiftKey) return true; // Mac ⌥←/→
            return false;
        },
    },
    navHome: {
        id: 'navHome',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'Home' && !e.ctrlKey && !e.metaKey && !e.shiftKey,
    },
    navCtrlHome: {
        id: 'navCtrlHome',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'Home' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey,
    },
    navCtrlEnd: {
        id: 'navCtrlEnd',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'End' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey,
    },
    openGoTo: {
        id: 'openGoTo',
        mutating: false,
        matches: (e, s) => !s.editing && ((lower(e.key) === 'g' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey) || e.key === 'F5'),
    },
    openFormatCells: {
        id: 'openFormatCells',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Digit1' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    toggleFilter: {
        id: 'toggleFilter',
        mutating: true,
        matches: (e, s) => !s.editing && lower(e.key) === 'l' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey),
    },
    autoSum: {
        id: 'autoSum',
        mutating: true,
        // Alt+= (Win/Mac), or Mac Shift+Cmd+T (Excel Mac's own AutoSum key —
        // reserved by Chrome/Safari to reopen a closed tab; kept anyway, same
        // as F4/cycleRef's Cmd+T, so a Keyboard-Lock context still gets it).
        matches: (e, s) =>
            !s.editing &&
            ((e.code === 'Equal' && Boolean(e.altKey) && !e.ctrlKey && !e.metaKey) ||
                (lower(e.key) === 't' && Boolean(e.metaKey) && Boolean(e.shiftKey) && !e.ctrlKey && !e.altKey)),
    },
    pasteValues: {
        id: 'pasteValues',
        mutating: true,
        matches: (e, s) => !s.editing && lower(e.key) === 'v' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey),
    },
    openPasteSpecial: {
        id: 'openPasteSpecial',
        mutating: true,
        // Win Ctrl+Alt+V; Mac Cmd+Ctrl+V; Mac Ctrl+Option+V (altKey IS
        // Option, so this is the same bit pattern as Win's Ctrl+Alt+V); Mac
        // Cmd+Option+V.
        matches: (e, s) =>
            !s.editing &&
            lower(e.key) === 'v' &&
            !e.shiftKey &&
            ((Boolean(e.ctrlKey) && Boolean(e.altKey)) ||
                (Boolean(e.metaKey) && Boolean(e.ctrlKey)) ||
                (Boolean(e.metaKey) && Boolean(e.altKey))),
    },
    enterMove: {
        id: 'enterMove',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey,
    },
    tabCommit: {
        id: 'tabCommit',
        mutating: true,
        // Tab/Shift+Tab commit+move right/left; Shift+Enter (editing) commits
        // and moves up. The formula-autocomplete Tab case (accept the
        // suggestion instead) is declined in dispatchLaneA, not here.
        matches: (e, s) => s.editing && (e.key === 'Tab' || (e.key === 'Enter' && Boolean(e.shiftKey))),
    },
    italic: {
        id: 'italic',
        mutating: true,
        matches: (e) => lower(e.key) === 'i' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    underline: {
        id: 'underline',
        mutating: true,
        matches: (e) => lower(e.key) === 'u' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    extendHome: {
        id: 'extendHome',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'Home' && Boolean(e.shiftKey) && !e.ctrlKey && !e.metaKey,
    },
    extendCtrlHome: {
        id: 'extendCtrlHome',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'Home' && Boolean(e.shiftKey) && Boolean(e.ctrlKey || e.metaKey),
    },
    extendCtrlEnd: {
        id: 'extendCtrlEnd',
        mutating: false,
        matches: (e, s) => !s.editing && e.key === 'End' && Boolean(e.shiftKey) && Boolean(e.ctrlKey || e.metaKey),
    },
    // `e.code` (layout-independent, mirrors autoSum's own Equal-by-code
    // match) intercepts these BEFORE the library's bubble-phase
    // handleKeydownForZoom ever sees them — Ctrl+Alt+=/- (real zoom) are
    // excluded via `!e.altKey` and pass through unchanged.
    deleteCells: {
        id: 'deleteCells',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Minus' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    insertCells: {
        id: 'insertCells',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Equal' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey) && !e.altKey,
    },
    hideRows: {
        id: 'hideRows',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Digit9' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    hideCols: {
        id: 'hideCols',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Digit0' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    unhideRows: {
        id: 'unhideRows',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Digit9' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey) && !e.altKey,
    },
    unhideCols: {
        id: 'unhideCols',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Digit0' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey) && !e.altKey,
    },
    insertDate: {
        id: 'insertDate',
        mutating: true,
        // Ctrl+; only — Mac Cmd+; is insertTime (below), not this.
        matches: (e, s) => !s.editing && e.code === 'Semicolon' && Boolean(e.ctrlKey) && !e.shiftKey && !e.metaKey && !e.altKey,
    },
    insertTime: {
        id: 'insertTime',
        mutating: true,
        // Windows Ctrl+Shift+; and Mac Cmd+; are the two documented Excel
        // combos; Mac Cmd+Shift+; isn't one of them, but it reaches Fortune's
        // own OLD full-timestamp handler exactly like the other two once
        // unmatched — claimed here too so that library path is never reached
        // on either platform.
        matches: (e, s) =>
            !s.editing &&
            e.code === 'Semicolon' &&
            !e.altKey &&
            ((Boolean(e.ctrlKey) && Boolean(e.shiftKey) && !e.metaKey) || (Boolean(e.metaKey) && !e.ctrlKey)),
    },
    copyAbove: {
        id: 'copyAbove',
        mutating: true,
        matches: (e, s) => !s.editing && e.code === 'Quote' && Boolean(e.ctrlKey || e.metaKey) && Boolean(e.shiftKey) && !e.altKey,
    },
    insertLink: {
        id: 'insertLink',
        mutating: true,
        matches: (e, s) => !s.editing && lower(e.key) === 'k' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    numFmtPreset: {
        id: 'numFmtPreset',
        mutating: true,
        matches: (e, s) =>
            !s.editing &&
            Boolean(e.ctrlKey || e.metaKey) &&
            Boolean(e.shiftKey) &&
            !e.altKey &&
            ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Backquote'].includes(e.code ?? ''),
    },
    openCellDropdown: {
        id: 'openCellDropdown',
        mutating: false,
        // Decides interception itself (a plain cell, or a filter/validation
        // cell that isn't actually a dropdown, isn't handled — see
        // sheetActions.ts's own doc comment).
        matches: (e, s) => !s.editing && e.key === 'ArrowDown' && Boolean(e.altKey) && !e.ctrlKey && !e.metaKey && !e.shiftKey,
    },
});

/** Frozen Lane B binding table (Ctrl/Cmd+S, Ctrl/Cmd+P, Escape). The
 *  focus-lost fallback is a 4th Lane B behaviour but is target-driven, not
 *  key-driven, so it is not a keymap entry — see `isFallbackTarget`. */
export const LANE_B: Readonly<Record<LaneBActionId, ShortcutBinding<LaneBActionId>>> = Object.freeze({
    save: {
        id: 'save',
        mutating: false,
        matches: (e) => lower(e.key) === 's' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    printGuard: {
        id: 'printGuard',
        mutating: false,
        matches: (e) => lower(e.key) === 'p' && Boolean(e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey,
    },
    resolveEscape: {
        id: 'resolveEscape',
        mutating: false,
        matches: (e) => e.key === 'Escape',
    },
});

/** The mouse-triggered fill-down — mutating, dispatched by the
 *  double-click handler directly rather than through `matchShortcut`. */
export const MOUSE_MUTATING_ACTIONS: ReadonlySet<MouseActionId> = new Set(['fillDownDblClick']);

/** Every mutating action id across both lanes plus the mouse binding — the
 *  historyOpen gate consults this. */
export const MUTATING_ACTIONS: ReadonlySet<ActionId> = new Set([
    ...Object.values(LANE_A)
        .filter((b) => b.mutating)
        .map((b) => b.id),
    ...Object.values(LANE_B)
        .filter((b) => b.mutating)
        .map((b) => b.id),
    ...MOUSE_MUTATING_ACTIONS,
]);

// Hoisted once (the tables are frozen and never change) rather than
// re-derived from Object.values on every keydown — matchShortcut runs on
// every keystroke from both lanes.
const LANE_A_LIST = Object.values(LANE_A);
const LANE_B_LIST = Object.values(LANE_B);

/** Pure lookup: the first (and by construction, only) binding in `lane` whose
 *  `matches` holds for `e`/`state`, or `null` when nothing matches. Overloaded
 *  on `lane` so a caller passing `'A'`/`'B'` gets back the narrower id type,
 *  not the full `ActionId` union. */
export function matchShortcut(e: KeyLike, lane: 'A', state: ShortcutMatchState): LaneAActionId | null;
export function matchShortcut(e: KeyLike, lane: 'B', state: ShortcutMatchState): LaneBActionId | null;
export function matchShortcut(e: KeyLike, lane: 'A' | 'B', state: ShortcutMatchState): ActionId | null {
    const list = lane === 'A' ? LANE_A_LIST : LANE_B_LIST;
    for (const binding of list) {
        if (binding.matches(e, state)) return binding.id;
    }
    return null;
}

// The grid's two special DOM ids live in selectors.ts (shared with
// useSheetFocus.ts — the cell input is also the focus-restore target there).
const GRID_TARGET_IDS = new Set([CELL_INPUT_ID, FORMULA_BAR_ID]);

// Shared with useSheetFocus.ts and useSheetKeyboardShortcuts.ts — this file
// is the single source of truth for what counts as an editable DOM target.
export const NATIVE_EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** True for a native form control or a contentEditable node — the generic
 *  "is this a typing target" check, independent of the grid's own ids. */
export const isNativeEditableElement = (el: Element | null): boolean => {
    if (!(el instanceof HTMLElement)) return false;
    if (NATIVE_EDITABLE_TAGS.has(el.tagName)) return true;
    // `isContentEditable` (not the `contenteditable` attribute) is the
    // correct check — it also covers inherited editability — but jsdom never
    // computes it, so tests set the attribute directly; check both.
    return el.isContentEditable || el.getAttribute('contenteditable') === 'true';
};

/**
 * Lane A's target gate. `onKeyDownCapture` on the wrapper
 * also fires for the library's OWN inputs (Find/Replace, dialog fields, the
 * add-rows input, the sheet-tab rename `contentEditable`), which the library
 * only shields with a bubble-phase `stopPropagation` — too late for a capture
 * listener. Lane A may act only when the target is the cell input, the
 * formula bar, or a non-editable node (the canvas/overlay).
 */
export const isGridKeyTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return true;
    if (GRID_TARGET_IDS.has(target.id)) return true;
    return !isNativeEditableElement(target);
};

/** Lane B's focus-lost fallback fires only when the event target is
 *  `document.body` itself — never a library input, a header control, or any
 *  other element ("document.body only", not "body/chrome"). */
export const isFallbackTarget = (target: EventTarget | null): boolean => target === document.body;
